/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React, { PropsWithChildren } from "react";
import { Button, Typography } from "@mui/material";
import { MinimalCase } from "../../../types/Entities/MinimalCase";
import { Link } from "react-router";
import { getCaseLink } from "../../../services/productService";
import { AbstractCase } from "../../../types/Entities/AbstractCase";
import AlertLegalbird from "@/src/components/legalbird/AlertLegalbird/AlertLegalbird";
import AlertContentLegalbird from "@/src/components/legalbird/AlertLegalbird/AlertContentLegalbird";

type ProductType = MinimalCase | AbstractCase;

type IncompleteCaseWarningProps = {
  product: ProductType;
};

export default function IncompleteCaseWarning({ product }: IncompleteCaseWarningProps) {
  if (product.isCustomerDataCompleted) {
    return null;
  }

  if (product.categoryChange) {
    return (
      <IncompleteCaseAlertBase product={product} title={"Angaben ergänzen für weitere Unterstützung"}>
        <Typography
          variant={"inherit"}
          sx={{
            marginBottom: "1rem",
          }}
        >
          In Ihrem Fall ist nach der Erstberatung nun weitere Unterstützung gewünscht.{" "}
          <strong>Damit wir weiter für Sie tätig werden können, müssen Sie bitte weitere Angaben zu Ihrem Fall ergänzen und uns die Vollmacht erteilen.</strong>
        </Typography>
        <Typography
          variant={"inherit"}
          sx={{
            marginBottom: "1rem",
          }}
        >
          Keine Sorge: Ihre bisherigen Angaben und Dokumente sind nach wie vor vorhanden. Sie können diese also gerne weiter benutzen oder gegebenenfalls um
          neue Informationen und Dokumente ergänzen.
        </Typography>
      </IncompleteCaseAlertBase>
    );
  }
  return (
    <IncompleteCaseAlertBase product={product} title={"Bitte vervollständigen Sie zuerst Ihren Fall"}>
      <Typography
        variant={"inherit"}
        sx={{
          marginBottom: "1rem",
        }}
      >
        Damit wir Ihnen helfen können, benötigen wir noch Angaben von Ihnen. <strong>Dies dauert keine 5 Minuten!</strong> Sobald uns Ihr Fall vollständig
        vorliegt, können Sie eine Beratung buchen, Ihren Fall prüfen oder Schreiben durch uns verfassen lassen.
      </Typography>
    </IncompleteCaseAlertBase>
  );
}

function IncompleteCaseAlertBase({ children, product, title }: PropsWithChildren<{ product: ProductType, title: string }>) {
  return (
    <AlertLegalbird severity={"warning"} sx={{ marginBottom: "1.5rem" }}>
      <AlertContentLegalbird
        title={title}
        description={
          <>
            {children}
            <Typography
              variant={"inherit"}
              sx={{
                marginBottom: "1rem",
              }}
            >
              <strong>Sie sind bei Angaben unsicher?</strong> Das ist gar kein Problem! Befüllen Sie alles bestmöglich und haben Sie keine Sorge vor „falschen“
              Angaben. Sie können uns im Formular per Freitext weitere Informationen und Hinweise geben. Ihr Fall wird immer von Experten geprüft, die jederzeit
              für Sie Angaben ergänzen und korrigieren können.
            </Typography>
            <Typography variant={"inherit"}>
              <strong>Klicken Sie zum Vervollständigen bitte auf:</strong>
            </Typography>
            <Button variant={"outlined"} color={"warning"} component={Link} to={getCaseLink(product) + "/antrag"} sx={{ marginTop: "1rem"}}>
              Fall vervollständigen
            </Button>
          </>
        }
      />
    </AlertLegalbird>
  );
}
